/**
 * React Query + API for CSV export templates (server-backed).
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToast } from '@/providers/ToastProvider';
import api from '@/services/api';

import { migrateLegacyCsvExportTemplatesIfNeeded } from '../../migrateLocalCsvExportTemplates';
import type { CsvExportTemplate } from '../../useCsvExportTemplates';

const BASE = '/csv-export-templates';

const keys = {
  all: ['csv-export-templates'] as const,
  byUserTab: (userId: string, tab: string) =>
    [...keys.all, userId, tab] as const,
};

export function useCsvExportTemplatesForTab(
  userId: string,
  tab: string,
  open: boolean,
) {
  return useQuery({
    queryKey: keys.byUserTab(userId, tab),
    queryFn: async () => {
      const res = await api.get<{ data: CsvExportTemplate[] }>(
        `${BASE}?tab=${encodeURIComponent(tab)}`,
      );
      let list = res.data.data;
      if (list.length === 0) {
        const migrated = await migrateLegacyCsvExportTemplatesIfNeeded(userId);
        if (migrated) {
          const res2 = await api.get<{ data: CsvExportTemplate[] }>(
            `${BASE}?tab=${encodeURIComponent(tab)}`,
          );
          list = res2.data.data;
        }
      }
      return list;
    },
    enabled: open && Boolean(userId),
  });
}

export interface CreateCsvExportTemplatePayload {
  tab: string;
  name: string;
  color: string;
  columns: string[];
  isDefault: boolean;
  downloadBaseName?: string;
}

export function useCreateCsvExportTemplate() {
  const qc = useQueryClient();
  const { success, error } = useToast();
  return useMutation({
    mutationFn: async (payload: CreateCsvExportTemplatePayload) => {
      const res = await api.post<{ data: CsvExportTemplate }>(BASE, {
        tab: payload.tab,
        name: payload.name,
        color: payload.color,
        columns: payload.columns,
        isDefault: payload.isDefault,
        ...(payload.downloadBaseName
          ? { downloadBaseName: payload.downloadBaseName }
          : {}),
      });
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.all });
      success('Template saved', 'Your CSV export template was saved to your account.');
    },
    onError: () => {
      error(
        'Save failed',
        'Could not save the template. Check permissions and try again.',
      );
    },
  });
}

export interface UpdateCsvExportTemplatePayload {
  id: string;
  name?: string;
  color?: string;
  columns?: string[];
  isDefault?: boolean;
  downloadBaseName?: string;
  tab?: string;
}

export function useUpdateCsvExportTemplate() {
  const qc = useQueryClient();
  const { success, error } = useToast();
  return useMutation({
    mutationFn: async (payload: UpdateCsvExportTemplatePayload) => {
      const { id, ...body } = payload;
      const res = await api.patch<{ data: CsvExportTemplate }>(
        `${BASE}/${id}`,
        body,
      );
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.all });
      success('Template updated', 'Changes to your CSV template were saved.');
    },
    onError: () => {
      error(
        'Update failed',
        'Could not update the template. Check permissions and try again.',
      );
    },
  });
}

export function useDeleteCsvExportTemplate() {
  const qc = useQueryClient();
  const { success, error } = useToast();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`${BASE}/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.all });
      success('Template removed', 'The CSV export template was deleted.');
    },
    onError: () => {
      error(
        'Delete failed',
        'Could not delete the template. Check permissions and try again.',
      );
    },
  });
}
